import { FC } from "react";
import { Box, Flex, HStack, Icon, Image, keyframes, Text, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiCheck, FiX } from "react-icons/fi";
import { SlideTone } from "./types";

// ── Paleta ────────────────────────────────────────────────────────────────
const C = {
  purple: "#430098",
  deep: "#2E0069",
  violet: "#6A1FC2",
  red: "#E51F44",
  cyan: "#22D3EE",
  gold: "#FFD23F",
  green: "#2FCE25",
  ink: "#1A1A2E"
};

const FONT = "-apple-system,'Segoe UI',Helvetica,Arial,sans-serif";

const isDark = (t?: SlideTone) => t === "dark" || t === "brand";

// ── Tło: gradient + pływające orby + delikatna siatka ───────────────────────
const Orb: FC<{ color: string; size: number; pos: Record<string, string>; delay?: number }> = ({
  color,
  size,
  pos,
  delay = 0
}) => (
  <motion.div
    style={{
      position: "absolute",
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      filter: "blur(70px)",
      ...pos
    }}
    animate={{ x: [0, 30, -10, 0], y: [0, -25, 15, 0], scale: [1, 1.08, 0.96, 1] }}
    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

export const Backdrop: FC<{ tone?: SlideTone }> = ({ tone }) => {
  const dark = isDark(tone);
  return (
    <Box position="absolute" inset={0} overflow="hidden">
      <Box
        position="absolute"
        inset={0}
        bgGradient={
          dark
            ? "linear(150deg, #25005A 0%, #430098 52%, #6A1FC2 100%)"
            : "radial(1200px 820px at 78% 18%, #F4EEFF 0%, #FFFFFF 62%)"
        }
      />
      <Orb color={dark ? "#22D3EE" : "#E9DEFF"} size={460} pos={{ top: "-140px", left: "-90px", opacity: dark ? "0.5" : "0.9" }} />
      <Orb color={dark ? "#EC4899" : "#FDE2EC"} size={560} pos={{ bottom: "-180px", right: "-130px", opacity: dark ? "0.45" : "0.9" }} delay={1.4} />
      <Orb color={dark ? "#A78BFA" : "#EAF7EF"} size={320} pos={{ top: "44%", left: "58%", opacity: dark ? "0.4" : "0.8" }} delay={0.8} />
      <Box
        position="absolute"
        inset={0}
        opacity={dark ? 0.1 : 0.05}
        backgroundImage="linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg,#000 1px, transparent 1px)"
        backgroundSize="52px 52px"
      />
    </Box>
  );
};

// ── Wspólne helpery animacji ────────────────────────────────────────────────
const draw = (delay = 0, duration = 1.3) => ({
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { duration, ease: "easeInOut", delay }
});

const SvgWrap: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center">
    <svg width="100%" height="100%" viewBox="0 0 520 520" style={{ maxWidth: 560, maxHeight: 560 }} fontFamily={FONT}>
      {children}
    </svg>
  </Box>
);

// ── Sceny ───────────────────────────────────────────────────────────────────

// Szklana urna wyborcza → pieczęć → łańcuch (slajd otwierający). Pełna przebudowa.
const BallotSeal: FC<{ dark: boolean }> = ({ dark }) => {
  const glassStroke = dark ? "rgba(255,255,255,0.92)" : "#430098";
  const lidFill = dark ? "rgba(255,255,255,0.94)" : "#EDE7FA";
  const slot = dark ? "#1E0E40" : "#430098";
  const chainStroke = dark ? "rgba(255,255,255,0.85)" : "#430098";
  const chainFill = dark ? "rgba(255,255,255,0.12)" : "#F1ECFB";
  const seal = "#E51F44";
  return (
    <SvgWrap>
      <defs>
        <linearGradient id="urnaGlass" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={dark ? 0.24 : 0.92} />
          <stop offset="100%" stopColor={dark ? "#ffffff" : "#E7DCFA"} stopOpacity={dark ? 0.05 : 0.96} />
        </linearGradient>
        <radialGradient id="urnaGlow" cx="50%" cy="46%" r="55%">
          <stop offset="0%" stopColor={seal} stopOpacity="0.30" />
          <stop offset="100%" stopColor={seal} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="chainGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={dark ? 0.2 : 1} />
          <stop offset="100%" stopColor={dark ? "#ffffff" : "#EDE4FB"} stopOpacity={dark ? 0.05 : 1} />
        </linearGradient>
      </defs>

      {/* poświata */}
      <circle cx="260" cy="285" r="195" fill="url(#urnaGlow)" />

      {/* spadająca karta (pętla) */}
      <motion.g
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: [-80, -6, -6, 6], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.2, times: [0, 0.34, 0.72, 1], repeat: Infinity, repeatDelay: 1.3, ease: "easeIn" }}
      >
        <rect x="206" y="116" width="108" height="74" rx="10" fill="#ffffff" stroke={seal} strokeWidth="3" />
        <rect x="224" y="139" width="72" height="7" rx="3.5" fill={seal} />
        <rect x="224" y="157" width="54" height="7" rx="3.5" fill="#B9B3C9" />
      </motion.g>

      {/* korpus urny (szkło) */}
      <rect x="170" y="214" width="180" height="172" rx="24" fill="url(#urnaGlass)" stroke={glassStroke} strokeWidth="3.5" />
      {/* refleks na szkle */}
      <path d="M196 232 L218 232 L250 372 L228 372 Z" fill="#ffffff" opacity={dark ? 0.1 : 0.55} />
      {/* karty wrzucone (widoczne przez szkło) */}
      <rect x="194" y="332" width="62" height="40" rx="6" fill="#ffffff" opacity={dark ? 0.18 : 0.6} transform="rotate(-8 225 352)" />
      <rect x="262" y="336" width="58" height="38" rx="6" fill="#ffffff" opacity={dark ? 0.12 : 0.42} transform="rotate(7 291 355)" />

      {/* pokrywa + szczelina */}
      <rect x="154" y="196" width="212" height="32" rx="13" fill={lidFill} stroke={glassStroke} strokeWidth="3" />
      <rect x="223" y="207" width="74" height="10" rx="5" fill={slot} />

      {/* pieczęć woskowa na froncie urny */}
      <motion.g initial={{ scale: 0, rotate: -18 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.9, type: "spring", stiffness: 150, damping: 12 }}>
        <motion.circle cx="260" cy="300" r="46" fill="none" stroke={seal} strokeWidth="3"
          animate={{ scale: [1, 1.42], opacity: [0.55, 0] }} transition={{ duration: 2, repeat: Infinity }} />
        <circle cx="260" cy="300" r="42" fill={seal} />
        <circle cx="260" cy="300" r="42" fill="none" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.55" strokeDasharray="3 5" />
        <motion.path d="M239 301 l13 14 l27 -32" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" {...draw(1.2, 0.6)} />
      </motion.g>

      {/* łącznik pieczęć → łańcuch */}
      <motion.line x1="260" y1="388" x2="260" y2="406" stroke={chainStroke} strokeWidth="4" strokeDasharray="5 6" {...draw(1.5, 0.4)} />

      {/* łańcuch bloków — nowy wygląd + zapętlony impuls walidacji */}
      {[0, 1, 2, 3].map((i) => {
        const bx = 150 + i * 72;
        return (
          <g key={i}>
            {i > 0 && (
              <motion.rect x={bx - 18} y="433" width="18" height="11" rx="5.5" fill={chainStroke} opacity="0.85"
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.7 + i * 0.16, duration: 0.25 }} style={{ transformOrigin: `${bx - 9}px 438px` }} />
            )}
            <motion.g initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.62 + i * 0.16, duration: 0.4 }}>
              <rect x={bx} y="411" width="54" height="56" rx="13" fill="url(#chainGrad)" stroke={chainStroke} strokeWidth="2.5" />
              <rect x={bx + 9} y="420" width="36" height="8" rx="4" fill={seal} opacity="0.92" />
              <rect x={bx + 9} y="435" width="36" height="5" rx="2.5" fill={chainStroke} opacity="0.45" />
              <rect x={bx + 9} y="445" width="24" height="5" rx="2.5" fill={chainStroke} opacity="0.3" />
            </motion.g>
          </g>
        );
      })}
    </SvgWrap>
  );
};

// Łańcuch bloków, środkowy z wbitą pieczęcią ✓ (slajd „idea"). Spokojnie, bez skakania.
const StampChain: FC<{ accent: string; dark: boolean }> = ({ dark }) => {
  const seal = "#E51F44";
  const stroke = dark ? "rgba(255,255,255,0.85)" : "#430098";
  return (
    <SvgWrap>
      <defs>
        <linearGradient id="scGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={dark ? 0.2 : 1} />
          <stop offset="100%" stopColor={dark ? "#ffffff" : "#EDE4FB"} stopOpacity={dark ? 0.05 : 1} />
        </linearGradient>
        <radialGradient id="scGlow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={seal} stopOpacity="0.2" />
          <stop offset="100%" stopColor={seal} stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="260" cy="270" r="180" fill="url(#scGlow)" />

      {/* łańcuch: 3 bloki, środkowy wyróżniony */}
      {[0, 1, 2].map((i) => {
        const bx = 158 + i * 74;
        const mid = i === 1;
        const by = mid ? 252 : 264;
        const bh = mid ? 72 : 56;
        return (
          <g key={i}>
            {i > 0 && (
              <motion.rect x={bx - 20} y="286" width="20" height="11" rx="5.5" fill={stroke} opacity="0.85"
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.5 + i * 0.18, duration: 0.25 }} style={{ transformOrigin: `${bx - 10}px 291px` }} />
            )}
            <motion.g initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.18, duration: 0.4 }}>
              <rect x={bx} y={by} width="58" height={bh} rx="14" fill="url(#scGrad)" stroke={mid ? seal : stroke} strokeWidth={mid ? 3.5 : 2.5} />
              {!mid && (
                <>
                  <rect x={bx + 10} y={by + 11} width="38" height="7" rx="3.5" fill={seal} opacity="0.85" />
                  <rect x={bx + 10} y={by + 25} width="38" height="5" rx="2.5" fill={stroke} opacity="0.4" />
                  <rect x={bx + 10} y={by + 35} width="24" height="5" rx="2.5" fill={stroke} opacity="0.28" />
                </>
              )}
            </motion.g>
          </g>
        );
      })}

      {/* pieczęć na środkowym bloku — jednorazowe przybicie + delikatny puls */}
      <motion.g initial={{ scale: 0, rotate: -16 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.85, type: "spring", stiffness: 150, damping: 12 }} style={{ transformOrigin: "260px 288px" }}>
        <motion.circle cx="260" cy="288" r="31" fill="none" stroke={seal} strokeWidth="3"
          animate={{ scale: [1, 1.55], opacity: [0.5, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }} />
        <circle cx="260" cy="288" r="28" fill={seal} />
        <circle cx="260" cy="288" r="28" fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.5" strokeDasharray="2.5 4.5" />
        <path d="M246 289 l10 10 l19 -22" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
    </SvgWrap>
  );
};

// Karta werdyktu jak wynik weryfikacji w aplikacji (slajd „dowód w kliknięciu").
const VerdictCard: FC = () => (
  <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center">
    <Box position="relative" w="380px" h="340px">
      {/* drobna czerwona karta „wykryto zmianę" — w tle */}
      <Box position="absolute" right="2px" top="28px" w="206px" bg="#fff" borderRadius="18px"
        border="1px solid #F3D0D8" boxShadow="0 16px 38px rgba(0,0,0,0.10)" p="15px" transform="rotate(7deg)" opacity={0.9}>
        <HStack spacing="10px">
          <Flex w="34px" h="34px" borderRadius="full" bg="#FCE7EC" align="center" justify="center" flexShrink={0}>
            <Icon as={FiX} boxSize="19px" color="#E51F44" />
          </Flex>
          <Box>
            <Text fontSize="13px" fontWeight="800" color="#E51F44" lineHeight="1.1">Wykryto zmianę</Text>
            <Text fontSize="11px" color="#8A8A96">ktoś naruszył wynik</Text>
          </Box>
        </HStack>
      </Box>

      {/* główna zielona karta „zgodne z łańcuchem" */}
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        style={{ position: "absolute", left: "0px", top: "104px", width: "256px" }}>
        <Box bg="#fff" borderRadius="22px" border="1px solid #E6E1F2" boxShadow="0 26px 60px rgba(67,0,152,0.18)" p="22px">
          <HStack spacing="14px" mb="15px">
            <Box position="relative" w="52px" h="52px" flexShrink={0}>
              <motion.div style={{ position: "absolute", inset: 0, borderRadius: "999px", border: "2px solid #2fce25" }}
                animate={{ scale: [1, 1.55], opacity: [0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
              <Flex w="52px" h="52px" borderRadius="full" bg="#2fce25" align="center" justify="center" boxShadow="0 8px 20px rgba(47,206,37,0.4)">
                <Icon as={FiCheck} boxSize="29px" color="#fff" />
              </Flex>
            </Box>
            <Box>
              <Text fontSize="18px" fontWeight="900" color="#11221F" lineHeight="1.1">Wynik prawdziwy</Text>
              <Text fontSize="13px" color="#1E9E5A" fontWeight="700">Nikt go nie zmienił</Text>
            </Box>
          </HStack>
          <Box bg="#F6F4FB" borderRadius="10px" px="12px" py="9px" mb="10px">
            <Text fontSize="10.5px" color="#8A8A96" fontWeight="600">Protokół wyniku · 12 480 głosów</Text>
          </Box>
          <Flex bg="#EAF9E9" borderRadius="10px" px="12px" py="10px" align="center" gap="8px">
            <Icon as={FiCheck} boxSize="15px" color="#1E9E5A" />
            <Text fontSize="12px" fontWeight="800" color="#1E9E5A" letterSpacing="0.05em">ZWERYFIKOWANO</Text>
          </Flex>
        </Box>
      </motion.div>
    </Box>
  </Box>
);

// Pływające telefony — odwzorowanie sekcji „aplikacja mobilna" ze strony głównej.
const phFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}`;

const PhoneBar: FC<{ label: string; pct: number; color: string }> = ({ label, pct, color }) => (
  <Box w="100%">
    <Flex justify="space-between" mb="2px">
      <Text fontSize="8px" color="rgba(255,255,255,0.85)" fontWeight="600">{label}</Text>
      <Text fontSize="8px" color="#fff" fontWeight="800">{pct}%</Text>
    </Flex>
    <Box w="100%" h="5px" bg="rgba(255,255,255,0.14)" borderRadius="full" overflow="hidden">
      <Box w={`${pct}%`} h="100%" bg={color} borderRadius="full" />
    </Box>
  </Box>
);

const PhoneScreen: FC = () => (
  <VStack spacing="9px" w="100%" align="stretch">
    <Image src="/LBP_Logo_White.svg" alt="" maxH="17px" objectFit="contain" alignSelf="center" opacity={0.9} mt="2px" />
    <Box bg="rgba(255,255,255,0.10)" border="1px solid rgba(255,255,255,0.18)" borderRadius="12px" p="11px">
      <HStack spacing="5px" mb="6px">
        <Box w="6px" h="6px" borderRadius="full" bg="#2fce25" />
        <Text fontSize="7px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase" color="rgba(255,255,255,0.65)">Trwa głosowanie</Text>
      </HStack>
      <Text fontSize="10px" fontWeight="800" color="#fff" mb="8px" noOfLines={2} lineHeight="1.25">Budżet obywatelski — edycja 2026</Text>
      <VStack spacing="6px" align="stretch">
        <PhoneBar label="Park i zieleń" pct={62} color="#2fce25" />
        <PhoneBar label="Ścieżki rowerowe" pct={28} color="#22D3EE" />
        <PhoneBar label="Plac zabaw" pct={10} color="#FFD23F" />
      </VStack>
    </Box>
    <Box bg="#fff" borderRadius="10px" py="8px" textAlign="center" boxShadow="0 6px 16px rgba(0,0,0,0.25)">
      <Text fontSize="10px" fontWeight="800" color="#430098">Zagłosuj teraz</Text>
    </Box>
  </VStack>
);

const Phone: FC<{ rotate: number; left: string; z: number; delay: string; dim?: boolean }> = ({ rotate, left, z, delay, dim }) => (
  <Box position="absolute" left={left} top="50%" zIndex={z} transform={`translateY(-50%) rotate(${rotate}deg)`}
    animation={`${phFloat} 6s ease-in-out infinite`} sx={{ animationDelay: delay }}>
    <Box position="relative" w="168px" h="336px" borderRadius="34px" bgGradient="linear(160deg,#1a0440,#2d0066)"
      border="2px solid rgba(255,255,255,0.20)" boxShadow="0 30px 64px rgba(0,0,0,0.5)" p="22px 15px" opacity={dim ? 0.96 : 1}>
      <Box position="absolute" top="11px" left="50%" transform="translateX(-50%)" w="34%" h="6px" bg="rgba(255,255,255,0.28)" borderRadius="full" />
      <PhoneScreen />
    </Box>
  </Box>
);

const FrontPhones: FC = () => (
  <Box position="absolute" inset={0}>
    <Phone left="40%" rotate={8} z={1} delay="0.7s" dim />
    <Phone left="4%" rotate={-5} z={2} delay="0s" />
  </Box>
);

// Instytucja: symetryczna budowla (równe kolumny) z emblematem ✓ w tympanonie.
const Institution: FC<{ accent: string; dark: boolean }> = ({ dark }) => {
  const line = dark ? "rgba(255,255,255,0.92)" : "#430098";
  const fill = dark ? "rgba(255,255,255,0.06)" : "#F1ECFB";
  const seal = "#E51F44";
  const colX = [168, 220, 272, 324];
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="instGlow" cx="50%" cy="44%" r="55%">
          <stop offset="0%" stopColor={seal} stopOpacity="0.15" />
          <stop offset="100%" stopColor={seal} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="260" cy="240" r="185" fill="url(#instGlow)" />

      <motion.path d="M150 162 L260 90 L370 162 Z" fill={fill} stroke={line} strokeWidth="3" strokeLinejoin="round"
        initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} />
      <motion.rect x="146" y="160" width="228" height="20" rx="3" fill={fill} stroke={line} strokeWidth="3"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.4 }} />

      {colX.map((x, i) => (
        <motion.g key={x} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 + i * 0.1, duration: 0.4 }}>
          <rect x={x - 4} y="182" width="34" height="9" rx="2" fill={fill} stroke={line} strokeWidth="2" />
          <rect x={x} y="191" width="26" height="146" fill={fill} stroke={line} strokeWidth="2" />
          <rect x={x - 4} y="334" width="34" height="9" rx="2" fill={fill} stroke={line} strokeWidth="2" />
        </motion.g>
      ))}

      <rect x="138" y="345" width="244" height="15" rx="3" fill={fill} stroke={line} strokeWidth="2.5" />
      <rect x="124" y="360" width="272" height="16" rx="3" fill={fill} stroke={line} strokeWidth="2.5" />

      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.9, type: "spring", stiffness: 160, damping: 12 }} style={{ transformOrigin: "260px 130px" }}>
        <motion.circle cx="260" cy="130" r="21" fill="none" stroke={seal} strokeWidth="2.5" animate={{ scale: [1, 1.5], opacity: [0.5, 0] }} transition={{ duration: 2.2, repeat: Infinity }} />
        <circle cx="260" cy="130" r="19" fill={seal} />
        <path d="M250 131 l7 7 l13 -15" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
    </SvgWrap>
  );
};

// Znak zapytania nad kartą — kruche zaufanie (slajd „problem").
const TrustQuestion: FC<{ accent: string; dark: boolean }> = ({ accent, dark }) => {
  const card = dark ? "rgba(255,255,255,0.1)" : "#fff";
  const stroke = dark ? "rgba(255,255,255,0.5)" : "#C9C2DE";
  return (
    <SvgWrap>
      <motion.rect x="150" y="200" width="220" height="150" rx="16" fill={card} stroke={stroke} strokeWidth="3"
        animate={{ rotate: [-2, 2, -2] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "260px 275px" }} />
      <line x1="178" y1="240" x2="300" y2="240" stroke={stroke} strokeWidth="6" strokeLinecap="round" />
      <line x1="178" y1="268" x2="270" y2="268" stroke={stroke} strokeWidth="6" strokeLinecap="round" />
      <motion.g initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5, type: "spring", stiffness: 140 }}>
        <circle cx="330" cy="180" r="52" fill={accent} />
        <text x="330" y="212" textAnchor="middle" fontSize="70" fontWeight="900" fill="#fff" fontFamily={FONT}>?</text>
      </motion.g>
    </SvgWrap>
  );
};

// Kontakt / pilotaż — rakieta startująca (slajd końcowy).
const Launch: FC<{ accent: string }> = ({ accent }) => (
  <SvgWrap>
    <motion.g initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: "easeOut" }}>
      <path d="M260 110 C300 150 300 230 260 300 C220 230 220 150 260 110 Z" fill="#fff" stroke={accent} strokeWidth="3" />
      <circle cx="260" cy="185" r="22" fill={accent} />
      <path d="M222 250 L196 300 L236 282 Z" fill={accent} />
      <path d="M298 250 L324 300 L284 282 Z" fill={accent} />
    </motion.g>
    <motion.path d="M260 305 C252 345 268 345 260 392" stroke={C.gold} strokeWidth="12" strokeLinecap="round"
      animate={{ opacity: [0.3, 1, 0.3], scaleY: [0.7, 1.2, 0.7] }} transition={{ duration: 0.8, repeat: Infinity }} style={{ transformOrigin: "260px 350px" }} />
    {[150, 200, 320, 370].map((x, i) => (
      <motion.circle key={x} cx={x} cy={140 + (i % 2) * 60} r="4" fill={accent}
        animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }} />
    ))}
  </SvgWrap>
);

// Czarna skrzynka — nieprzezroczysta urna z „?", przeciwieństwo slajdu 1 (slajd „problem").
const BlackBox: FC = () => {
  const stroke = "#2A2A36";
  const seal = "#E51F44";
  const qs = [
    { x: 116, y: 150, d: 0 },
    { x: 402, y: 176, d: 0.6 },
    { x: 150, y: 392, d: 1.1 }
  ];
  return (
    <SvgWrap>
      <defs>
        <linearGradient id="bbBody" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#3C3C4A" />
          <stop offset="100%" stopColor="#22222E" />
        </linearGradient>
        <radialGradient id="bbGlow" cx="50%" cy="46%" r="55%">
          <stop offset="0%" stopColor={seal} stopOpacity="0.22" />
          <stop offset="100%" stopColor={seal} stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="260" cy="290" r="195" fill="url(#bbGlow)" />

      {qs.map((q, i) => (
        <motion.text
          key={i} x={q.x} y={q.y} fontSize="40" fontWeight="900" fill={seal} opacity="0.5" fontFamily={FONT}
          animate={{ y: [q.y, q.y - 12, q.y], opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 3, repeat: Infinity, delay: q.d, ease: "easeInOut" }}
        >
          ?
        </motion.text>
      ))}

      <motion.g animate={{ rotate: [-1.6, 1.6, -1.6] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "260px 290px" }}>
        <rect x="170" y="206" width="180" height="176" rx="24" fill="url(#bbBody)" stroke={stroke} strokeWidth="3.5" />
        <rect x="154" y="190" width="212" height="32" rx="13" fill="#2C2C38" stroke={stroke} strokeWidth="3" />
        <rect x="223" y="201" width="74" height="10" rx="5" fill="#15151D" />
        <motion.g initial={{ scale: 0, rotate: -16 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.5, type: "spring", stiffness: 150, damping: 12 }}>
          <motion.circle cx="260" cy="300" r="46" fill="none" stroke={seal} strokeWidth="3" animate={{ scale: [1, 1.4], opacity: [0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
          <circle cx="260" cy="300" r="42" fill={seal} />
          <text x="260" y="319" textAnchor="middle" fontSize="52" fontWeight="900" fill="#fff" fontFamily={FONT}>?</text>
        </motion.g>
      </motion.g>
    </SvgWrap>
  );
};

// ════════ Sceny dla decku „samorządy" ════════

// Ratusz + mieszkańcy + flaga (hero samorządowy).
const CivicHero: FC = () => {
  const line = "rgba(255,255,255,0.92)";
  const fill = "rgba(255,255,255,0.08)";
  const gold = "#FFD23F";
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="chGlow" cx="50%" cy="44%" r="55%">
          <stop offset="0%" stopColor={gold} stopOpacity="0.16" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="260" cy="250" r="195" fill="url(#chGlow)" />
      {/* boczne kamienice */}
      {[{ x: 92 }, { x: 344 }].map((b, i) => (
        <motion.g key={b.x} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}>
          <rect x={b.x} y="226" width="84" height="150" rx="6" fill={fill} stroke={line} strokeWidth="2.5" />
          {[238, 268, 298, 328].map((wy) =>
            [0, 1].map((c) => <rect key={`${wy}-${c}`} x={b.x + 16 + c * 36} y={wy} width="22" height="16" rx="2" fill="rgba(255,255,255,0.16)" />)
          )}
        </motion.g>
      ))}
      {/* ratusz */}
      <motion.g initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <path d="M184 198 L260 138 L336 198 Z" fill={fill} stroke={line} strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="196" y="198" width="128" height="178" rx="4" fill={fill} stroke={line} strokeWidth="2.5" />
        {[214, 248, 282].map((x) => <rect key={x} x={x} y="222" width="22" height="86" rx="3" fill="rgba(255,255,255,0.12)" stroke={line} strokeWidth="1.5" />)}
        <rect x="242" y="320" width="36" height="56" rx="3" fill="rgba(255,255,255,0.16)" stroke={line} strokeWidth="2" />
        <circle cx="260" cy="172" r="12" fill="none" stroke={gold} strokeWidth="2.5" />
        <line x1="260" y1="172" x2="260" y2="165" stroke={gold} strokeWidth="2" /><line x1="260" y1="172" x2="266" y2="175" stroke={gold} strokeWidth="2" />
        <line x1="260" y1="138" x2="260" y2="112" stroke={line} strokeWidth="2.5" />
        <path d="M260 114 L288 121 L260 130 Z" fill="#E51F44" />
      </motion.g>
      {/* mieszkańcy */}
      {[206, 234, 262, 290, 318].map((x, i) => (
        <motion.g key={x} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.08 }}>
          <circle cx={x} cy="396" r="8" fill={gold} />
          <path d={`M${x - 9} 422 q9 -16 18 0 Z`} fill={gold} />
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Frekwencja: 10 osób, tylko 3 dotarły do urny (≈31%).
const TurnoutGauge: FC = () => {
  const red = "#E51F44";
  const grey = "#C7C0D6";
  const person = (cx: number, cy: number, filled: boolean) => {
    const col = filled ? red : grey;
    return (
      <g>
        <circle cx={cx} cy={cy} r="13" fill={filled ? col : "#fff"} stroke={col} strokeWidth="3" />
        <path d={`M${cx - 18} ${cy + 48} q0 -28 18 -28 q18 0 18 28 Z`} fill={filled ? col : "#fff"} stroke={col} strokeWidth="3" />
      </g>
    );
  };
  const cols = [140, 200, 260, 320, 380];
  return (
    <SvgWrap>
      <motion.text x="260" y="118" textAnchor="middle" fontSize="78" fontWeight="900" fill={red} fontFamily={FONT}
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
        31%
      </motion.text>
      <text x="260" y="150" textAnchor="middle" fontSize="19" fontWeight="700" fill="#6B6B7B" fontFamily={FONT}>
        frekwencja przy papierze
      </text>
      {[0, 1].map((r) =>
        cols.map((x, i) => {
          const idx = r * 5 + i;
          return (
            <motion.g key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + idx * 0.05, duration: 0.35 }}>
              {person(x, 212 + r * 100, idx < 3)}
            </motion.g>
          );
        })
      )}
    </SvgWrap>
  );
};

// Dedykowane glify projektów budżetu (drzewo / rower / balon).
const budgetGlyph = (g: string, cx: number, cy: number, color: string) => {
  if (g === "tree")
    return (
      <g>
        <rect x={cx - 2.5} y={cy + 1} width="5" height="10" rx="1.5" fill={color} />
        <circle cx={cx} cy={cy - 3} r="9" fill={color} />
      </g>
    );
  if (g === "bike")
    return (
      <g fill="none" stroke={color} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx={cx - 7.5} cy={cy + 4} r="5.5" />
        <circle cx={cx + 7.5} cy={cy + 4} r="5.5" />
        <path d={`M${cx - 7.5} ${cy + 4} L${cx - 1} ${cy - 5} L${cx + 7.5} ${cy + 4}`} />
        <path d={`M${cx - 1} ${cy - 5} L${cx + 4} ${cy - 5}`} />
      </g>
    );
  return (
    <g>
      <circle cx={cx} cy={cy - 3} r="8.5" fill={color} />
      <path d={`M${cx} ${cy + 5} q3 4 0 8`} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </g>
  );
};

// Budżet obywatelski — projekty z paskami głosów i monetami.
const ParticipatoryBudget: FC = () => {
  const purple = "#430098";
  const projects = [
    { name: "Park i zieleń", pct: 62, color: "#2fce25", g: "tree" },
    { name: "Ścieżki rowerowe", pct: 41, color: "#22D3EE", g: "bike" },
    { name: "Plac zabaw", pct: 28, color: "#FFB020", g: "balloon" }
  ];
  const votes = ["1 240", "820", "560"];
  return (
    <SvgWrap>
      {/* nagłówek: portfel (line) + tytuł + status */}
      <motion.g initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <rect x="120" y="78" width="30" height="22" rx="5" fill="none" stroke={purple} strokeWidth="2.5" />
        <path d="M120 85 h30" stroke={purple} strokeWidth="2.5" />
        <circle cx="143" cy="91" r="2.6" fill={purple} />
        <text x="162" y="96" fontSize="20" fontWeight="800" fill={purple} fontFamily={FONT}>Budżet obywatelski 2026</text>
        <circle cx="370" cy="132" r="5" fill="#2fce25" />
        <text x="382" y="137" fontSize="13" fontWeight="700" fill="#27a91f" fontFamily={FONT}>na żywo</text>
      </motion.g>
      {projects.map((p, i) => {
        const y = 156 + i * 84;
        return (
          <motion.g key={p.name} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.15, duration: 0.4 }}>
            <rect x="116" y={y} width="296" height="70" rx="15" fill={i === 0 ? "#F1FBF0" : "#fff"} stroke={i === 0 ? "#BFE9BC" : "#E6E1F2"} strokeWidth="2" />
            <rect x="132" y={y + 17} width="40" height="40" rx="11" fill={p.color} opacity="0.16" />
            {budgetGlyph(p.g, 152, y + 37, p.color)}
            <text x="190" y={y + 30} fontSize="16.5" fontWeight="800" fill="#1A1A2E" fontFamily={FONT}>{p.name}</text>
            <rect x="190" y={y + 42} width="156" height="9" rx="4.5" fill="#EFEAF8" />
            <motion.rect x="190" y={y + 42} height="9" rx="4.5" fill={p.color}
              initial={{ width: 0 }} animate={{ width: (156 * p.pct) / 100 }} transition={{ delay: 0.5 + i * 0.15, duration: 0.7 }} />
            <text x="392" y={y + 28} textAnchor="end" fontSize="17" fontWeight="900" fill={p.color} fontFamily={FONT}>{p.pct}%</text>
            <text x="392" y={y + 48} textAnchor="end" fontSize="11.5" fontWeight="600" fill="#9A93AC" fontFamily={FONT}>{votes[i]} gł.</text>
          </motion.g>
        );
      })}
    </SvgWrap>
  );
};

// Konsultacje — dymki opinii spływają do urny.
const consultIcon = (t: string, cx: number, cy: number, c: string) => {
  if (t === "doc")
    return (
      <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round">
        <rect x={cx - 9} y={cy - 12} width="18" height="24" rx="2.5" />
        <line x1={cx - 4} y1={cy - 5} x2={cx + 5} y2={cy - 5} />
        <line x1={cx - 4} y1={cy} x2={cx + 5} y2={cy} />
        <line x1={cx - 4} y1={cy + 5} x2={cx + 2} y2={cy + 5} />
      </g>
    );
  if (t === "building")
    return (
      <g fill="none" stroke={c} strokeWidth="2.2">
        <rect x={cx - 11} y={cy - 12} width="22" height="24" rx="2" />
        {[-7, 1].map((wy) => [-6, 2].map((wx) => <rect key={`${wx}-${wy}`} x={cx + wx} y={cy + wy} width="4" height="4" />))}
      </g>
    );
  if (t === "bubble")
    return (
      <g>
        <rect x={cx - 12} y={cy - 10} width="24" height="17" rx="6" fill={c} />
        <path d={`M${cx - 5} ${cy + 7} l0 6 l6 -6 Z`} fill={c} />
        {[-5, 0, 5].map((dx) => <circle key={dx} cx={cx + dx} cy={cy - 2} r="2" fill="#fff" />)}
      </g>
    );
  if (t === "people")
    return (
      <g fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round">
        <circle cx={cx - 6} cy={cy - 5} r="4.5" />
        <circle cx={cx + 6} cy={cy - 5} r="4.5" />
        <path d={`M${cx - 13} ${cy + 10} q7 -11 14 0`} />
        <path d={`M${cx - 1} ${cy + 10} q7 -11 14 0`} />
      </g>
    );
  if (t === "pin")
    return (
      <g>
        <path d={`M${cx} ${cy - 13} c -9 0 -11 11 0 24 c 11 -13 9 -24 0 -24 Z`} fill={c} />
        <circle cx={cx} cy={cy - 4} r="4" fill="#fff" />
      </g>
    );
  return <path d={`M${cx - 9} ${cy} l6 7 l13 -15`} fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />;
};

// Konsultacje: centralny pojemnik + ikony tematów dookoła (hub-and-spoke).
const Consultation: FC = () => {
  const cx0 = 260;
  const cy0 = 252;
  const R = 146;
  const items = [
    { t: "doc", c: "#7c4dff" },
    { t: "building", c: "#22D3EE" },
    { t: "bubble", c: "#2fce25" },
    { t: "check", c: "#E51F44" },
    { t: "people", c: "#FFB020" },
    { t: "pin", c: "#8b2fd6" }
  ];
  const pos = items.map((_, i) => {
    const a = ((-90 + i * 60) * Math.PI) / 180;
    return { x: cx0 + R * Math.cos(a), y: cy0 + R * Math.sin(a) };
  });
  return (
    <SvgWrap>
      {pos.map((p, i) => (
        <motion.line key={`s${i}`} x1={cx0} y1={cy0} x2={p.x} y2={p.y} stroke={items[i].c} strokeWidth="2" strokeDasharray="3 6" opacity="0.4"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }} />
      ))}
      {/* centralny pojemnik */}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 150, damping: 13 }}>
        <circle cx={cx0} cy={cy0} r="60" fill="#430098" opacity="0.07" />
        <rect x={cx0 - 44} y={cy0 - 34} width="88" height="74" rx="18" fill="#fff" stroke="#430098" strokeWidth="3" />
        <rect x={cx0 - 22} y={cy0 - 44} width="44" height="11" rx="5.5" fill="#430098" />
        <path d={`M${cx0 - 17} ${cy0 + 6} l12 12 l22 -26`} fill="none" stroke="#430098" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
      {/* ikony tematów */}
      {pos.map((p, i) => (
        <motion.g key={i} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + i * 0.1, type: "spring", stiffness: 200, damping: 14 }}>
          <circle cx={p.x} cy={p.y} r="30" fill="#fff" stroke={items[i].c} strokeWidth="2.5" />
          {consultIcon(items[i].t, p.x, p.y, items[i].c)}
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Głosowanie rady — radni (sylwetki) w łuku + karta wyniku z plakietką „Przyjęto".
const CouncilVote: FC = () => {
  const green = "#2fce25";
  const red = "#E51F44";
  const grey = "#A7AAB5";
  const rows = [
    { label: "ZA", val: 14, color: green },
    { label: "PRZECIW", val: 5, color: red },
    { label: "WSTRZ.", val: 2, color: grey }
  ];
  const SEATS = 21;
  const seatColor = (i: number) => (i < 14 ? green : i < 19 ? red : grey);
  return (
    <SvgWrap>
      {/* radni w łuku */}
      {Array.from({ length: SEATS }).map((_, i) => {
        const ang = Math.PI - (i / (SEATS - 1)) * Math.PI;
        const cx = 260 + Math.cos(ang) * 152;
        const cy = 224 - Math.sin(ang) * 116;
        const col = seatColor(i);
        return (
          <motion.g key={i} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 + i * 0.03, type: "spring", stiffness: 210, damping: 15 }}>
            <circle cx={cx} cy={cy} r="6.5" fill={col} />
            <path d={`M${cx - 8.5} ${cy + 17} q8.5 -11 17 0 Z`} fill={col} />
          </motion.g>
        );
      })}
      {/* karta wyniku */}
      <rect x="110" y="278" width="300" height="166" rx="16" fill="#fff" stroke="#E6E1F2" strokeWidth="2" />
      <text x="130" y="306" fontSize="15" fontWeight="800" fill="#1A1A2E" fontFamily={FONT}>Wynik głosowania</text>
      <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2, type: "spring", stiffness: 170, damping: 13 }}>
        <rect x="300" y="290" width="92" height="26" rx="13" fill="#EAF9E9" />
        <path d="M312 303 l5 5 l9 -10" fill="none" stroke="#1E9E5A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <text x="330" y="307" fontSize="12.5" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>Przyjęto</text>
      </motion.g>
      <line x1="130" y1="322" x2="390" y2="322" stroke="#EFEAF8" strokeWidth="2" />
      {rows.map((r, i) => {
        const y = 342 + i * 32;
        const w = (r.val / SEATS) * 156;
        return (
          <g key={r.label}>
            <text x="130" y={y + 12} fontSize="13.5" fontWeight="800" fill="#1A1A2E" fontFamily={FONT}>{r.label}</text>
            <rect x="220" y={y + 2} width="156" height="12" rx="6" fill="#EFEAF8" />
            <motion.rect x="220" y={y + 2} height="12" rx="6" fill={r.color}
              initial={{ width: 0 }} animate={{ width: w }} transition={{ delay: 0.7 + i * 0.15, duration: 0.6 }} />
            <text x="392" y={y + 12} textAnchor="end" fontSize="14" fontWeight="900" fill={r.color} fontFamily={FONT}>{r.val}</text>
          </g>
        );
      })}
    </SvgWrap>
  );
};

// Wdrożenie „pod klucz" — checklista tego, co robimy po naszej stronie.
const CostCompare: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const rows = ["Hosting i serwery", "Utrzymanie 24/7", "Aktualizacje i bezpieczeństwo", "Wsparcie po polsku", "Bez własnej serwerowni"];
  return (
    <SvgWrap>
      <rect x="100" y="110" width="320" height="302" rx="22" fill="#fff" stroke="#E6E1F2" strokeWidth="2" />

      {/* nagłówek: klucz „pod klucz" + tytuł */}
      <g stroke={purple} strokeWidth="2.5" fill="none" strokeLinecap="round">
        <circle cx="128" cy="150" r="7" />
        <path d="M135 150 h17" />
        <path d="M145 150 v6 M151 150 v6" />
      </g>
      <text x="164" y="156" fontSize="19" fontWeight="800" fill={purple} fontFamily={FONT}>Po naszej stronie</text>
      <line x1="124" y1="176" x2="396" y2="176" stroke="#EFEAF8" strokeWidth="2" />

      {rows.map((r, i) => {
        const y = 210 + i * 42;
        return (
          <motion.g key={r} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.12, duration: 0.35 }}>
            <circle cx="138" cy={y} r="13" fill={green} />
            <path d={`M131 ${y} l5 5 l9 -10`} fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <text x="162" y={y + 5} fontSize="16" fontWeight="700" fill="#1A1A2E" fontFamily={FONT}>{r}</text>
          </motion.g>
        );
      })}
    </SvgWrap>
  );
};

// Suwerenność (hero): tarcza narodowa + mini-flaga PL + własna sieć 3 węzłów.
const SovereignHero: FC = () => {
  const line = "rgba(255,255,255,0.95)";
  const fill = "rgba(255,255,255,0.07)";
  const gold = "#FFD23F";
  const nodes = [
    { x: 260, y: 232 },
    { x: 220, y: 298 },
    { x: 300, y: 298 }
  ];
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="shGlow" cx="50%" cy="44%" r="55%">
          <stop offset="0%" stopColor={gold} stopOpacity="0.2" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="260" cy="252" r="196" fill="url(#shGlow)" />
      {/* tarcza */}
      <motion.path d="M168 140 Q260 128 352 140 L352 252 C352 326 305 366 260 392 C215 366 168 326 168 252 Z"
        fill={fill} stroke={line} strokeWidth="3.5" strokeLinejoin="round"
        initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} />
      {/* mini-flaga PL */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
        <rect x="228" y="158" width="64" height="13" rx="3" fill="#fff" />
        <rect x="228" y="171" width="64" height="13" rx="3" fill="#E51F44" />
        <rect x="228" y="158" width="64" height="26" rx="3" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
      </motion.g>
      {/* sieć węzłów */}
      <motion.path d={`M${nodes[0].x} ${nodes[0].y} L${nodes[1].x} ${nodes[1].y} L${nodes[2].x} ${nodes[2].y} Z`}
        fill="none" stroke={gold} strokeWidth="2.5" strokeOpacity="0.7"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.7, duration: 0.8 }} />
      {nodes.map((n, i) => (
        <motion.g key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.9 + i * 0.12, type: "spring", stiffness: 200, damping: 13 }}>
          {i === 0 && (
            <motion.circle cx={n.x} cy={n.y} r="14" fill="none" stroke={gold} strokeWidth="2.5"
              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }} transition={{ duration: 2, repeat: Infinity }} />
          )}
          <circle cx={n.x} cy={n.y} r="13" fill={gold} />
          <circle cx={n.x} cy={n.y} r="5" fill="#2E0069" />
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Zależność od obcego, zamkniętego dostawcy — nasze dane wypływają do „czarnej skrzynki".
const ForeignDep: FC = () => {
  const purple = "#430098";
  const red = "#E51F44";
  return (
    <SvgWrap>
      {/* nasza strona */}
      <motion.g initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
        <rect x="92" y="214" width="110" height="98" rx="14" fill="#fff" stroke={purple} strokeWidth="2.5" />
        <rect x="114" y="232" width="30" height="7" rx="2" fill="#fff" stroke="#CFC7E2" strokeWidth="1" />
        <rect x="114" y="239" width="30" height="7" rx="2" fill="#E51F44" />
        <rect x="114" y="262" width="62" height="6" rx="3" fill="#D9D2E6" />
        <rect x="114" y="276" width="46" height="6" rx="3" fill="#E4DEF0" />
        <text x="147" y="334" textAnchor="middle" fontSize="14" fontWeight="700" fill={purple} fontFamily={FONT}>u nas</text>
      </motion.g>

      {/* przepływ danych na zewnątrz */}
      <motion.line x1="210" y1="263" x2="292" y2="263" stroke={red} strokeWidth="3" strokeDasharray="7 6"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.4, duration: 0.6 }} />
      <path d="M292 256 l12 7 l-12 7 Z" fill={red} />
      <text x="252" y="248" textAnchor="middle" fontSize="13" fontWeight="700" fill={red} fontFamily={FONT}>dane · kontrola</text>

      {/* obcy serwer — czarna skrzynka */}
      <motion.g initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25, duration: 0.4 }}>
        <rect x="312" y="176" width="120" height="168" rx="16" fill="#2A2A36" />
        {[0, 1, 2].map((k) => (
          <g key={k}>
            <rect x="328" y={196 + k * 26} width="88" height="14" rx="3" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
            <circle cx={406} cy={203 + k * 26} r="3" fill="#777" />
          </g>
        ))}
        {/* kłódka (zamknięty kod) */}
        <g stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round">
          <rect x="358" y="290" width="28" height="22" rx="4" fill="rgba(255,255,255,0.12)" />
          <path d="M364 290 v-6 a8 8 0 0 1 16 0 v6" />
        </g>
        <circle cx="372" cy="301" r="2.6" fill="#fff" />
        <text x="372" y="334" textAnchor="middle" fontSize="13" fontWeight="700" fill="rgba(255,255,255,0.8)" fontFamily={FONT}>obcy dostawca</text>
      </motion.g>

      {/* ostrzeżenie */}
      <motion.g initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9, type: "spring", stiffness: 180, damping: 12 }}>
        <path d="M414 156 L433 190 L395 190 Z" fill={red} stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
        <rect x="411.5" y="168" width="5" height="11" rx="2.5" fill="#fff" />
        <circle cx="414" cy="184" r="2.6" fill="#fff" />
      </motion.g>
    </SvgWrap>
  );
};

// Własny łańcuch Fundacji — łańcuch bloków + flaga PL + „klucz po naszej stronie".
const OwnChain: FC = () => {
  const purple = "#430098";
  const seal = "#E51F44";
  return (
    <SvgWrap>
      <g>
        <rect x="178" y="120" width="26" height="10" rx="3" fill="#fff" stroke="#CFC7E2" strokeWidth="1" />
        <rect x="178" y="130" width="26" height="10" rx="3" fill="#E51F44" />
        <text x="214" y="135" fontSize="18" fontWeight="800" fill={purple} fontFamily={FONT}>Polski łańcuch</text>
      </g>
      {[0, 1, 2, 3].map((i) => {
        const bx = 104 + i * 78;
        return (
          <motion.g key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.14, duration: 0.4 }}>
            {i > 0 && (
              <g stroke={purple} strokeWidth="2.6" fill="none">
                <ellipse cx={bx - 14} cy="252" rx="8.5" ry="6" />
                <ellipse cx={bx - 6} cy="252" rx="8.5" ry="6" />
              </g>
            )}
            <rect x={bx} y="224" width="58" height="56" rx="13" fill="#F4F0FB" stroke={purple} strokeWidth="2.5" />
            <rect x={bx + 10} y="234" width="38" height="7" rx="3.5" fill={seal} opacity="0.85" />
            <rect x={bx + 10} y="248" width="38" height="5" rx="2.5" fill={purple} opacity="0.4" />
            <rect x={bx + 10} y="258" width="24" height="5" rx="2.5" fill={purple} opacity="0.28" />
          </motion.g>
        );
      })}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.9, type: "spring", stiffness: 160, damping: 12 }} style={{ transformOrigin: "360px 332px" }}>
        <g stroke={purple} strokeWidth="3.5" fill="none" strokeLinecap="round">
          <circle cx="348" cy="332" r="13" />
          <path d="M361 332 h33" />
          <path d="M384 332 v12 M394 332 v12" />
        </g>
        <circle cx="348" cy="332" r="4.5" fill={purple} />
      </motion.g>
      <text x="360" y="372" textAnchor="middle" fontSize="13" fontWeight="700" fill={purple} fontFamily={FONT}>klucz po naszej stronie</text>
    </SvgWrap>
  );
};

// Otwarty kod — okno edytora z kolorowym kodem + otwarta kłódka.
const OpenCode: FC = () => {
  const green = "#2fce25";
  const codeLines = [
    { y: 196, ind: 0, toks: [{ w: 40, c: "#C792EA" }, { w: 58, c: "#82AAFF" }, { w: 28, c: "#C3E88D" }] },
    { y: 218, ind: 20, toks: [{ w: 50, c: "#82AAFF" }, { w: 34, c: "#F78C6C" }] },
    { y: 240, ind: 20, toks: [{ w: 30, c: "#C792EA" }, { w: 68, c: "#C3E88D" }] },
    { y: 262, ind: 40, toks: [{ w: 44, c: "#82AAFF" }, { w: 40, c: "#FFCB6B" }, { w: 22, c: "#C3E88D" }] },
    { y: 284, ind: 20, toks: [{ w: 36, c: "#C792EA" }] },
    { y: 306, ind: 0, toks: [{ w: 50, c: "#82AAFF" }, { w: 30, c: "#C3E88D" }] }
  ];
  return (
    <SvgWrap>
      <rect x="120" y="140" width="280" height="198" rx="16" fill="#1E1633" />
      <rect x="120" y="140" width="280" height="34" rx="16" fill="#2A1E47" />
      <rect x="120" y="160" width="280" height="14" fill="#2A1E47" />
      <circle cx="142" cy="157" r="5" fill="#FF5F57" />
      <circle cx="160" cy="157" r="5" fill="#FEBC2E" />
      <circle cx="178" cy="157" r="5" fill="#28C840" />
      <text x="384" y="162" textAnchor="end" fontSize="12" fill="rgba(255,255,255,0.5)" fontFamily={FONT}>node.src</text>
      {codeLines.map((ln, i) => {
        let x = 140 + ln.ind;
        return (
          <motion.g key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 + i * 0.08 }}>
            {ln.toks.map((t, k) => {
              const el = <rect key={k} x={x} y={ln.y} width={t.w} height="9" rx="3" fill={t.c} opacity="0.92" />;
              x += t.w + 8;
              return el;
            })}
          </motion.g>
        );
      })}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.7, type: "spring", stiffness: 160, damping: 12 }}>
        <circle cx="382" cy="330" r="30" fill="#fff" stroke={green} strokeWidth="2.5" />
        <rect x="372" y="330" width="20" height="16" rx="3" fill="none" stroke={green} strokeWidth="2.6" />
        <path d="M376 330 v-5 a7 7 0 0 1 14 0" fill="none" stroke={green} strokeWidth="2.6" strokeLinecap="round" />
      </motion.g>
    </SvgWrap>
  );
};

// Trzy węzły, zasada 2-z-3 — serwery w trójkącie + status + badge.
const NodeCluster: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const grey = "#9aa0ab";
  const nodes = [
    { x: 260, y: 152, ok: true },
    { x: 166, y: 312, ok: true },
    { x: 354, y: 312, ok: false }
  ];
  const server = (cx: number, cy: number, col: string) => (
    <g>
      <rect x={cx - 26} y={cy - 30} width="52" height="60" rx="9" fill="#fff" stroke={col} strokeWidth="2.5" />
      {[0, 1, 2].map((k) => (
        <g key={k}>
          <rect x={cx - 18} y={cy - 20 + k * 16} width="36" height="9" rx="2" fill={col} opacity="0.14" />
          <circle cx={cx + 12} cy={cy - 15.5 + k * 16} r="2.4" fill={col} />
        </g>
      ))}
    </g>
  );
  return (
    <SvgWrap>
      {[
        [260, 152, 166, 312],
        [260, 152, 354, 312],
        [166, 312, 354, 312]
      ].map((c, i) => (
        <motion.line key={`c${i}`} x1={c[0]} y1={c[1]} x2={c[2]} y2={c[3]} stroke="#7c4dff" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="2 9"
          initial={{ opacity: 0 }} animate={{ opacity: 0.75, strokeDashoffset: [0, -22] }}
          transition={{ opacity: { duration: 0.5, delay: 0.4 }, strokeDashoffset: { duration: 1.2, repeat: Infinity, ease: "linear" } }} />
      ))}
      {nodes.map((n, i) => (
        <motion.g key={i} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 + i * 0.15, type: "spring", stiffness: 180, damping: 13 }}>
          {server(n.x, n.y, purple)}
          <circle cx={n.x + 22} cy={n.y - 28} r="11" fill={n.ok ? green : grey} />
          {n.ok ? (
            <path d={`M${n.x + 16} ${n.y - 28} l4 4 l8 -9`} fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <line x1={n.x + 18} y1={n.y - 28} x2={n.x + 26} y2={n.y - 28} stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
          )}
        </motion.g>
      ))}
      <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9, type: "spring", stiffness: 170, damping: 13 }}>
        <rect x="220" y="248" width="80" height="34" rx="17" fill="#EAF9E9" stroke="#BFE9BC" strokeWidth="2" />
        <path d="M236 265 l5 5 l9 -10" fill="none" stroke="#1E9E5A" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        <text x="272" y="270" textAnchor="middle" fontSize="15" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>2 z 3</text>
      </motion.g>
    </SvgWrap>
  );
};

// Dane w Polsce i UE — tarcza z flagą PL, gwiazdkami UE i kłódką.
const DataPl: FC = () => {
  const purple = "#430098";
  const gold = "#FFD23F";
  return (
    <SvgWrap>
      <motion.path d="M168 150 Q260 138 352 150 L352 258 C352 330 305 368 260 392 C215 368 168 330 168 258 Z"
        fill="#F4F0FB" stroke={purple} strokeWidth="3" strokeLinejoin="round"
        initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} />
      <rect x="232" y="170" width="56" height="13" rx="3" fill="#fff" stroke="#E6E1F2" strokeWidth="1" />
      <rect x="232" y="183" width="56" height="13" rx="3" fill="#E51F44" />
      {Array.from({ length: 7 }).map((_, i) => {
        const a = Math.PI - (i / 6) * Math.PI;
        const sx = 260 + Math.cos(a) * 66;
        const sy = 246 - Math.sin(a) * 30;
        return (
          <text key={i} x={sx} y={sy} textAnchor="middle" fontSize="13" fill={gold} fontFamily={FONT}>
            ★
          </text>
        );
      })}
      <g stroke={purple} strokeWidth="3" fill="none" strokeLinecap="round">
        <rect x="244" y="300" width="32" height="26" rx="5" fill="#fff" />
        <path d="M250 300 v-7 a10 10 0 0 1 20 0 v7" />
      </g>
      <circle cx="260" cy="313" r="3.2" fill={purple} />
      <text x="260" y="358" textAnchor="middle" fontSize="15" fontWeight="800" fill={purple} fontFamily={FONT}>PL · UE</text>
    </SvgWrap>
  );
};

// Bezpieczeństwo (hero): oko przekreślone (tajność) + zielony ✓ (sprawdzalność).
const SecretHero: FC = () => {
  const white = "rgba(255,255,255,0.95)";
  const gold = "#FFD23F";
  const green = "#2fce25";
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="secGlow" cx="50%" cy="46%" r="55%">
          <stop offset="0%" stopColor={gold} stopOpacity="0.18" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="258" cy="252" r="196" fill="url(#secGlow)" />
      <circle cx="258" cy="252" r="122" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
      {/* kłódka — tajność */}
      <motion.g initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <path d="M226 250 v-24 a32 32 0 0 1 64 0 v24" fill="none" stroke={white} strokeWidth="5" strokeLinecap="round" />
        <rect x="210" y="250" width="96" height="82" rx="16" fill="rgba(255,255,255,0.1)" stroke={white} strokeWidth="5" />
        <circle cx="258" cy="284" r="9" fill={white} />
        <rect x="254" y="289" width="8" height="22" rx="4" fill={white} />
      </motion.g>
      {/* check — sprawdzalność */}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8, type: "spring", stiffness: 160, damping: 12 }}>
        <circle cx="316" cy="324" r="30" fill={green} stroke="#fff" strokeWidth="4" />
        <path d="M302 324 l9 9 l18 -21" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
    </SvgWrap>
  );
};

// Paradoks: tajność + sprawdzalność współistnieją (dwie kafle + „+").
const Paradox: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const eyeOff = (cx: number, cy: number, col: string) => (
    <g fill="none" stroke={col} strokeWidth="2.6" strokeLinecap="round">
      <path d={`M${cx - 16} ${cy} Q${cx} ${cy - 12} ${cx + 16} ${cy} Q${cx} ${cy + 12} ${cx - 16} ${cy} Z`} />
      <circle cx={cx} cy={cy} r="5" />
      <line x1={cx - 15} y1={cy + 11} x2={cx + 15} y2={cy - 11} />
    </g>
  );
  return (
    <SvgWrap>
      <motion.g initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
        <rect x="92" y="190" width="150" height="120" rx="18" fill="#fff" stroke={purple} strokeWidth="2.5" />
        <circle cx="167" cy="236" r="26" fill="#F1ECFB" />
        {eyeOff(167, 236, purple)}
        <text x="167" y="290" textAnchor="middle" fontSize="15" fontWeight="800" fill={purple} fontFamily={FONT}>Tajność</text>
      </motion.g>
      <motion.g initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.4 }}>
        <rect x="278" y="190" width="150" height="120" rx="18" fill="#fff" stroke={green} strokeWidth="2.5" />
        <circle cx="353" cy="236" r="26" fill="#EAF9E9" />
        <path d="M340 236 l9 9 l17 -20" fill="none" stroke={green} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <text x="353" y="290" textAnchor="middle" fontSize="14.5" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>Sprawdzalność</text>
      </motion.g>
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: "spring", stiffness: 180, damping: 12 }}>
        <circle cx="260" cy="250" r="24" fill="#1A1A2E" />
        <path d="M260 240 v20 M250 250 h20" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
      </motion.g>
    </SvgWrap>
  );
};

// ZKP: zapieczętowana koperta (tajny głos) → „bez otwierania" → ✓ ważny. (dopracowana)
const ZkpWitness: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const red = "#E51F44";
  return (
    <SvgWrap>
      <defs>
        <linearGradient id="envBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBF9FF" />
          <stop offset="100%" stopColor="#ECE4FA" />
        </linearGradient>
      </defs>
      {/* koperta */}
      <motion.g initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
        <ellipse cx="190" cy="332" rx="96" ry="10" fill="#000" opacity="0.06" />
        <rect x="94" y="198" width="192" height="128" rx="16" fill="url(#envBody)" stroke={purple} strokeWidth="3" />
        <path d="M118 326 L190 280 L262 326" fill="none" stroke={purple} strokeOpacity="0.22" strokeWidth="2" />
        <path d="M100 202 L280 202 L190 274 Z" fill="#E3D7F7" stroke={purple} strokeWidth="3" strokeLinejoin="round" />
        {/* woskowa pieczęć */}
        <circle cx="190" cy="274" r="26" fill={red} stroke="#B5142E" strokeWidth="2.5" />
        <circle cx="190" cy="274" r="26" fill="none" stroke="#fff" strokeOpacity="0.4" strokeWidth="1.4" strokeDasharray="2.5 4.5" />
        <g stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round">
          <rect x="181" y="274" width="18" height="13" rx="3" />
          <path d="M184 274 v-4.5 a6 6 0 0 1 12 0 v4.5" />
        </g>
      </motion.g>
      <text x="190" y="350" textAnchor="middle" fontSize="14" fontWeight="800" fill={purple} fontFamily={FONT}>tajny głos</text>

      {/* konektor „bez otwierania" */}
      <motion.line x1="300" y1="256" x2="346" y2="256" stroke={green} strokeWidth="3.5" strokeLinecap="round" strokeDasharray="2 8"
        initial={{ opacity: 0 }} animate={{ opacity: 1, strokeDashoffset: [0, -20] }}
        transition={{ opacity: { delay: 0.4, duration: 0.4 }, strokeDashoffset: { duration: 1, repeat: Infinity, ease: "linear" } }} />
      <path d="M346 248 l13 8 l-13 8 Z" fill={green} />
      <rect x="294" y="224" width="72" height="22" rx="11" fill="#EAF9E9" />
      <text x="330" y="239" textAnchor="middle" fontSize="11" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>bez otwierania</text>

      {/* werdykt ✓ ważny */}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.7, type: "spring", stiffness: 160, damping: 12 }}>
        <motion.circle cx="404" cy="256" r="36" fill="none" stroke={green} strokeWidth="3"
          animate={{ scale: [1, 1.4], opacity: [0.5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
        <circle cx="404" cy="256" r="34" fill={green} stroke="#fff" strokeWidth="3" />
        <path d="M388 256 l11 11 l19 -23" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
      <text x="404" y="316" textAnchor="middle" fontSize="14" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>ważny</text>
    </SvgWrap>
  );
};

// Nullifier: jeden głos (zielony) + druga próba odrzucona (czerwony).
const Nullifier: FC = () => {
  const green = "#2fce25";
  const red = "#E51F44";
  return (
    <SvgWrap>
      <motion.g initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
        <rect x="120" y="168" width="206" height="76" rx="14" fill="#EAF9E9" stroke={green} strokeWidth="2.5" />
        <circle cx="160" cy="206" r="19" fill={green} />
        <path d="M152 206 l6 6 l12 -14" fill="none" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
        <text x="194" y="201" fontSize="16.5" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>Głos oddany</text>
        <text x="194" y="223" fontSize="12.5" fontWeight="600" fill="#5b6b68" fontFamily={FONT}>raz, ważny</text>
      </motion.g>
      <motion.g initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.4 }}>
        <rect x="154" y="278" width="206" height="76" rx="14" fill="#FCE7EC" stroke={red} strokeWidth="2.5" />
        <circle cx="194" cy="316" r="19" fill={red} />
        <path d="M187 309 l14 14 M201 309 l-14 14" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" />
        <text x="228" y="311" fontSize="16.5" fontWeight="800" fill={red} fontFamily={FONT}>Druga próba</text>
        <text x="228" y="333" fontSize="12.5" fontWeight="600" fill="#9A6B70" fontFamily={FONT}>odrzucona</text>
      </motion.g>
    </SvgWrap>
  );
};

// Separacja: lista uprawnionych ↔ głosy, brak mapowania (przerwany link z X).
const Separation: FC = () => {
  const purple = "#430098";
  const red = "#E51F44";
  const person = (cx: number, cy: number) => (
    <g fill="none" stroke={purple} strokeWidth="2.2" strokeLinecap="round">
      <circle cx={cx} cy={cy} r="5" />
      <path d={`M${cx - 7} ${cy + 14} q7 -10 14 0`} />
    </g>
  );
  return (
    <SvgWrap>
      <rect x="90" y="168" width="142" height="164" rx="16" fill="#fff" stroke={purple} strokeWidth="2.5" />
      <text x="161" y="194" textAnchor="middle" fontSize="14" fontWeight="800" fill={purple} fontFamily={FONT}>Kto głosował</text>
      {[0, 1, 2].map((r) => (
        <g key={r}>
          {person(120, 230 + r * 32)}
          <rect x="138" y={224 + r * 32} width="76" height="9" rx="4.5" fill="#E4DEF0" />
        </g>
      ))}
      <rect x="288" y="168" width="142" height="164" rx="16" fill="#fff" stroke={purple} strokeWidth="2.5" />
      <text x="359" y="194" textAnchor="middle" fontSize="14" fontWeight="800" fill={purple} fontFamily={FONT}>Jak głosował</text>
      {[0, 1, 2].map((r) => (
        <g key={r}>
          <rect x="306" y={222 + r * 32} width="14" height="14" rx="3" fill="#7c4dff" opacity="0.7" />
          <rect x="328" y={224 + r * 32} width="84" height="9" rx="4.5" fill="#E4DEF0" />
        </g>
      ))}
      <line x1="232" y1="250" x2="288" y2="250" stroke={red} strokeWidth="3" strokeDasharray="6 6" />
      <circle cx="260" cy="250" r="18" fill="#fff" stroke={red} strokeWidth="2.5" />
      <path d="M253 243 l14 14 M267 243 l-14 14" stroke={red} strokeWidth="2.6" strokeLinecap="round" />
    </SvgWrap>
  );
};

// Prywatność: tarcza + zamknięta kłódka + plakietka RODO.
const PrivacyShield: FC = () => {
  const purple = "#430098";
  return (
    <SvgWrap>
      <motion.path d="M168 150 Q260 138 352 150 L352 258 C352 330 305 368 260 392 C215 368 168 330 168 258 Z"
        fill="#F4F0FB" stroke={purple} strokeWidth="3" strokeLinejoin="round"
        initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} />
      <g fill="none" stroke={purple} strokeWidth="3.5" strokeLinecap="round">
        <rect x="232" y="228" width="56" height="48" rx="9" fill="#fff" />
        <path d="M242 228 v-12 a18 18 0 0 1 36 0 v12" />
      </g>
      <circle cx="260" cy="248" r="5" fill={purple} />
      <path d="M260 252 v11" stroke={purple} strokeWidth="3.5" strokeLinecap="round" />
      <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, type: "spring", stiffness: 170, damping: 13 }}>
        <rect x="222" y="300" width="76" height="30" rx="15" fill="#EAF9E9" stroke="#BFE9BC" strokeWidth="2" />
        <path d="M236 315 l5 5 l9 -10" fill="none" stroke="#1E9E5A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        <text x="272" y="320" fontSize="14" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>RODO</text>
      </motion.g>
    </SvgWrap>
  );
};

// Hero „jak to działa": płynąca ścieżka z węzłami, ostatni = zielony ✓.
const FlowHero: FC = () => {
  const gold = "#FFD23F";
  const green = "#2fce25";
  const nodes = [
    { x: 132, y: 300 },
    { x: 224, y: 212 },
    { x: 320, y: 286 },
    { x: 412, y: 202 }
  ];
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="flGlow" cx="50%" cy="44%" r="55%">
          <stop offset="0%" stopColor={gold} stopOpacity="0.16" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="270" cy="252" r="196" fill="url(#flGlow)" />
      <motion.path d="M132 300 C 180 230, 200 222, 224 212 C 286 188, 300 286, 320 286 C 372 286, 384 224, 412 202" fill="none" stroke={gold} strokeWidth="3.5" strokeDasharray="2 9" strokeLinecap="round"
        initial={{ opacity: 0 }} animate={{ opacity: 0.85, strokeDashoffset: [0, -22] }}
        transition={{ opacity: { duration: 0.5 }, strokeDashoffset: { duration: 1.2, repeat: Infinity, ease: "linear" } }} />
      {nodes.map((n, i) => {
        const last = i === nodes.length - 1;
        return (
          <motion.g key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 200, damping: 13 }}>
            <circle cx={n.x} cy={n.y} r="17" fill={last ? green : gold} stroke="#2E0069" strokeWidth={last ? 0 : 0} />
            {last ? (
              <path d={`M${n.x - 8} ${n.y} l5 5 l11 -13`} fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <circle cx={n.x} cy={n.y} r="7" fill="#2E0069" />
            )}
          </motion.g>
        );
      })}
    </SvgWrap>
  );
};

// Pipeline 4 kroków: Głos → Odcisk → Pieczęć → Łańcuch.
const VoteFlow: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const red = "#E51F44";
  const cy = 248;
  const xs = [96, 200, 304, 408];
  const steps = [
    { label: "Głos", g: "vote" },
    { label: "Odcisk", g: "finger" },
    { label: "Pieczęć", g: "seal" },
    { label: "Łańcuch", g: "chain" }
  ];
  const glyph = (g: string, cx: number) => {
    if (g === "vote")
      return (
        <g>
          <rect x={cx - 13} y={cy - 15} width="26" height="30" rx="3" fill="none" stroke={purple} strokeWidth="2.2" />
          <path d={`M${cx - 6} ${cy} l4 4 l8 -9`} fill="none" stroke={green} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    if (g === "finger")
      return (
        <g fill="none" stroke={purple} strokeWidth="2" strokeLinecap="round">
          <path d={`M${cx - 10} ${cy + 2} a10 12 0 0 1 20 0`} />
          <path d={`M${cx - 6} ${cy + 4} a6 8 0 0 1 12 0`} />
          <path d={`M${cx} ${cy - 6} v14`} />
        </g>
      );
    if (g === "seal")
      return (
        <g>
          <circle cx={cx} cy={cy} r="12" fill={red} />
          <path d={`M${cx - 5} ${cy} l4 4 l7 -8`} fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      );
    return (
      <g fill="none" stroke={purple} strokeWidth="2.2">
        <rect x={cx - 15} y={cy - 7} width="13" height="14" rx="3" />
        <rect x={cx + 2} y={cy - 7} width="13" height="14" rx="3" />
        <line x1={cx - 2} y1={cy} x2={cx + 2} y2={cy} />
      </g>
    );
  };
  return (
    <SvgWrap>
      {[0, 1, 2].map((i) => (
        <g key={`c${i}`}>
          <line x1={xs[i] + 34} y1={cy} x2={xs[i + 1] - 38} y2={cy} stroke={purple} strokeWidth="2.5" strokeDasharray="2 7" opacity="0.55" />
          <path d={`M${xs[i + 1] - 38} ${cy - 6} l9 6 l-9 6 Z`} fill={purple} opacity="0.6" />
        </g>
      ))}
      {steps.map((s, i) => (
        <motion.g key={i} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 + i * 0.15, type: "spring", stiffness: 180, damping: 13 }}>
          <circle cx={xs[i]} cy={cy} r="32" fill="#F4F0FB" stroke={purple} strokeWidth="2.5" />
          {glyph(s.g, xs[i])}
          <circle cx={xs[i] + 23} cy={cy - 24} r="11" fill={purple} />
          <text x={xs[i] + 23} y={cy - 20} textAnchor="middle" fontSize="12" fontWeight="800" fill="#fff" fontFamily={FONT}>{i + 1}</text>
          <text x={xs[i]} y={cy + 58} textAnchor="middle" fontSize="14" fontWeight="800" fill={purple} fontFamily={FONT}>{s.label}</text>
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Krok 1: telefon z ekranem głosowania + tap.
const StepVote: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  return (
    <SvgWrap>
      <rect x="186" y="128" width="148" height="262" rx="26" fill="#fff" stroke={purple} strokeWidth="3" />
      <rect x="244" y="142" width="32" height="6" rx="3" fill="#D9D2E6" />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="206" y={178 + i * 46} width="108" height="36" rx="9" fill={i === 0 ? "#EAF9E9" : "#F4F0FB"} stroke={i === 0 ? green : "#E6E1F2"} strokeWidth="2" />
          <circle cx="224" cy={196 + i * 46} r="8" fill="none" stroke={i === 0 ? green : "#C9C0E0"} strokeWidth="2.5" />
          {i === 0 && <circle cx="224" cy={196 + i * 46} r="4" fill={green} />}
          <rect x="240" y={192 + i * 46} width="56" height="8" rx="4" fill={i === 0 ? "#BFE9BC" : "#E4DEF0"} />
        </g>
      ))}
      <rect x="206" y="330" width="108" height="38" rx="10" fill={purple} />
      <text x="260" y="354" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily={FONT}>Zagłosuj</text>
      <motion.circle cx="260" cy="349" r="14" fill="none" stroke={purple} strokeWidth="2.5"
        animate={{ scale: [0.6, 2.3], opacity: [0.7, 0] }} transition={{ duration: 1.6, repeat: Infinity }} />
    </SvgWrap>
  );
};

// Krok 2: głos → odcisk palca (hash).
const StepHash: FC = () => {
  const purple = "#430098";
  return (
    <SvgWrap>
      <rect x="84" y="206" width="96" height="120" rx="12" fill="#F4F0FB" stroke={purple} strokeWidth="2.5" />
      <rect x="100" y="226" width="48" height="7" rx="3.5" fill="#D9D2E6" />
      <rect x="100" y="242" width="36" height="6" rx="3" fill="#E4DEF0" />
      <text x="132" y="300" textAnchor="middle" fontSize="12" fontWeight="700" fill={purple} fontFamily={FONT}>głos</text>
      <line x1="190" y1="262" x2="222" y2="262" stroke={purple} strokeWidth="2.5" strokeDasharray="5 5" />
      <path d="M222 256 l10 6 l-10 6 Z" fill={purple} />
      <circle cx="288" cy="262" r="40" fill="#F4F0FB" stroke={purple} strokeWidth="2.5" />
      <g fill="none" stroke={purple} strokeWidth="2.4" strokeLinecap="round">
        <path d="M270 262 a18 22 0 0 1 36 0" />
        <path d="M276 266 a12 15 0 0 1 24 0" />
        <path d="M282 270 a6 8 0 0 1 12 0" />
        <path d="M288 248 v22" />
      </g>
      <line x1="332" y1="262" x2="356" y2="262" stroke={purple} strokeWidth="2.5" strokeDasharray="5 5" />
      <path d="M356 256 l10 6 l-10 6 Z" fill={purple} />
      <rect x="372" y="246" width="68" height="32" rx="8" fill="#1E1633" />
      <text x="406" y="266" textAnchor="middle" fontSize="12.5" fontWeight="700" fill="#C3E88D" fontFamily={FONT}>a1f3…7b</text>
    </SvgWrap>
  );
};

// Krok 3: odcisk dołączany jako zapieczętowany blok łańcucha.
const StepSeal: FC = () => {
  const purple = "#430098";
  const red = "#E51F44";
  const nbx = 110 + 3 * 72;
  return (
    <SvgWrap>
      {[0, 1, 2].map((i) => {
        const bx = 110 + i * 72;
        return (
          <g key={i}>
            {i > 0 && <rect x={bx - 18} y="246" width="18" height="11" rx="5.5" fill={purple} opacity="0.85" />}
            <rect x={bx} y="224" width="54" height="56" rx="13" fill="#F4F0FB" stroke={purple} strokeWidth="2.5" />
            <rect x={bx + 9} y="236" width="36" height="6" rx="3" fill={purple} opacity="0.4" />
            <rect x={bx + 9} y="248" width="24" height="5" rx="2.5" fill={purple} opacity="0.28" />
          </g>
        );
      })}
      <rect x={nbx - 18} y="246" width="18" height="11" rx="5.5" fill={purple} opacity="0.85" />
      <motion.g initial={{ opacity: 0, scale: 0.82 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, type: "spring", stiffness: 160, damping: 12 }}>
        <rect x={nbx} y="220" width="58" height="64" rx="14" fill="#fff" stroke={red} strokeWidth="3" />
        <circle cx={nbx + 29} cy="252" r="16" fill={red} />
        <path d={`M${nbx + 21} 252 l6 6 l11 -13`} fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
      <motion.g initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: [0, 1, 1, 0] }} transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.8, times: [0, 0.3, 0.7, 1] }}>
        <rect x={nbx + 4} y="150" width="50" height="24" rx="6" fill="#1E1633" />
        <text x={nbx + 29} y="166" textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#C3E88D" fontFamily={FONT}>a1f3…</text>
      </motion.g>
    </SvgWrap>
  );
};

// Dwa tryby: tajny (kłódka, ZKP) i jawny (oko, eksplorator).
const TwoModes: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  return (
    <SvgWrap>
      <path d="M260 150 L162 198 M260 150 L358 198" stroke={purple} strokeWidth="2.5" strokeDasharray="5 5" fill="none" opacity="0.5" />
      <circle cx="260" cy="150" r="10" fill={purple} />
      <rect x="88" y="200" width="152" height="152" rx="18" fill="#fff" stroke={purple} strokeWidth="2.5" />
      <circle cx="164" cy="248" r="27" fill="#F1ECFB" />
      <g stroke={purple} strokeWidth="2.8" fill="none" strokeLinecap="round">
        <rect x="153" y="246" width="22" height="17" rx="3" />
        <path d="M157 246 v-5 a7 7 0 0 1 14 0 v5" />
      </g>
      <text x="164" y="302" textAnchor="middle" fontSize="15" fontWeight="800" fill={purple} fontFamily={FONT}>Tajny</text>
      <text x="164" y="324" textAnchor="middle" fontSize="11.5" fontWeight="600" fill="#6B6B7B" fontFamily={FONT}>ZKP · ŻE, nie JAK</text>
      <rect x="280" y="200" width="152" height="152" rx="18" fill="#fff" stroke={green} strokeWidth="2.5" />
      <circle cx="356" cy="248" r="27" fill="#EAF9E9" />
      <g fill="none" stroke="#1E9E5A" strokeWidth="2.8" strokeLinecap="round">
        <path d="M338 248 Q356 232 374 248 Q356 264 338 248 Z" />
        <circle cx="356" cy="248" r="5.5" />
      </g>
      <text x="356" y="302" textAnchor="middle" fontSize="15" fontWeight="800" fill="#1E9E5A" fontFamily={FONT}>Jawny</text>
      <text x="356" y="324" textAnchor="middle" fontSize="11.5" fontWeight="600" fill="#6B6B7B" fontFamily={FONT}>w eksploratorze</text>
    </SvgWrap>
  );
};

// Roadmapa — oś czasu z kamieniami (zrobione = zielony ✓, plan = obrys).
const Roadmap: FC = () => {
  const purple = "#430098";
  const green = "#2fce25";
  const ms = [
    { label: "Łańcuch 3 węzły", done: true },
    { label: "ZKP w węźle", done: true },
    { label: "Publiczne API", done: false },
    { label: "Tożsamość / OAuth", done: false }
  ];
  const xs = [112, 210, 308, 406];
  const y = 250;
  return (
    <SvgWrap>
      <line x1="96" y1={y} x2="424" y2={y} stroke="#E1DAF0" strokeWidth="3" strokeLinecap="round" />
      <motion.line x1="96" y1={y} x2="210" y2={y} stroke={green} strokeWidth="3" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.8 }} />
      {ms.map((m, i) => (
        <motion.g key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 200, damping: 13 }}>
          <circle cx={xs[i]} cy={y} r="16" fill={m.done ? green : "#fff"} stroke={m.done ? green : "#C9C0E0"} strokeWidth="3" />
          {m.done ? (
            <path d={`M${xs[i] - 7} ${y} l5 5 l9 -10`} fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <circle cx={xs[i]} cy={y} r="5" fill="#C9C0E0" />
          )}
          <text x={xs[i]} y={i % 2 === 0 ? y - 30 : y + 42} textAnchor="middle" fontSize="12.5" fontWeight="700" fill={m.done ? "#1E9E5A" : purple} fontFamily={FONT}>
            {m.label}
          </text>
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Hero organizacji: wspólnota (ludzie wokół) + wspólna decyzja ✓.
const CommunityHero: FC = () => {
  const gold = "#FFD23F";
  const green = "#2fce25";
  const white = "rgba(255,255,255,0.92)";
  const cx0 = 260;
  const cy0 = 252;
  const ppl = Array.from({ length: 6 }).map((_, i) => {
    const a = ((i * 60 - 90) * Math.PI) / 180;
    return { x: cx0 + Math.cos(a) * 112, y: cy0 + Math.sin(a) * 112 };
  });
  const person = (cx: number, cy: number) => (
    <g>
      <circle cx={cx} cy={cy - 6} r="9" fill={gold} />
      <path d={`M${cx - 13} ${cy + 16} q13 -16 26 0 Z`} fill={gold} />
    </g>
  );
  return (
    <SvgWrap>
      <defs>
        <radialGradient id="chmGlow" cx="50%" cy="44%" r="55%">
          <stop offset="0%" stopColor={gold} stopOpacity="0.16" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={cx0} cy={cy0} r="196" fill="url(#chmGlow)" />
      {ppl.map((p, i) => (
        <line key={`l${i}`} x1={cx0} y1={cy0} x2={p.x} y2={p.y} stroke={gold} strokeWidth="1.5" strokeDasharray="2 6" opacity="0.4" />
      ))}
      <circle cx={cx0} cy={cy0} r="40" fill="rgba(255,255,255,0.1)" stroke={white} strokeWidth="2.5" />
      <path d={`M${cx0 - 16} ${cy0} l11 11 l21 -25`} fill="none" stroke={green} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      {ppl.map((p, i) => (
        <motion.g key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.1, type: "spring", stiffness: 200, damping: 13 }}>
          {person(p.x, p.y)}
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Papierowa udręka: rozjechany stos kart + zegar + „kworum?".
const PaperPain: FC = () => {
  const purple = "#430098";
  const red = "#E51F44";
  const sheets = [
    { r: -8, x: 142, y: 214 },
    { r: 7, x: 168, y: 202 },
    { r: -2, x: 156, y: 192 }
  ];
  return (
    <SvgWrap>
      {sheets.map((s, i) => (
        <g key={i} transform={`rotate(${s.r} ${s.x + 50} ${s.y + 62})`}>
          <rect x={s.x} y={s.y} width="100" height="124" rx="8" fill="#fff" stroke={purple} strokeWidth="2.5" />
          <rect x={s.x + 16} y={s.y + 18} width="50" height="7" rx="3.5" fill="#D9D2E6" />
          <rect x={s.x + 16} y={s.y + 34} width="36" height="6" rx="3" fill="#E4DEF0" />
          <rect x={s.x + 16} y={s.y + 56} width="16" height="16" rx="3" fill="none" stroke={purple} strokeWidth="2" />
        </g>
      ))}
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4, type: "spring", stiffness: 170, damping: 12 }}>
        <circle cx="352" cy="198" r="30" fill="#fff" stroke={red} strokeWidth="3" />
        <path d="M352 198 v-16 M352 198 l12 8" stroke={red} strokeWidth="3" strokeLinecap="round" fill="none" />
      </motion.g>
      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: "spring", stiffness: 170, damping: 12 }}>
        <circle cx="360" cy="318" r="28" fill={red} />
        <text x="360" y="330" textAnchor="middle" fontSize="34" fontWeight="900" fill="#fff" fontFamily={FONT}>?</text>
      </motion.g>
      <text x="360" y="366" textAnchor="middle" fontSize="13" fontWeight="700" fill={red} fontFamily={FONT}>kworum?</text>
    </SvgWrap>
  );
};

// Typy organizacji — 4 kafle z ikonami.
const OrgTypes: FC = () => {
  const purple = "#430098";
  const icon = (t: string, cx: number, cy: number) => {
    if (t === "building")
      return (
        <g fill="none" stroke={purple} strokeWidth="2.2">
          <rect x={cx - 11} y={cy - 12} width="22" height="24" rx="2" />
          {[-7, 1].map((wy) => [-6, 2].map((wx) => <rect key={`${wx}${wy}`} x={cx + wx} y={cy + wy} width="4" height="4" />))}
        </g>
      );
    if (t === "cap")
      return (
        <g fill="none" stroke={purple} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round">
          <path d={`M${cx} ${cy - 11} l17 7 l-17 7 l-17 -7 Z`} />
          <path d={`M${cx + 11} ${cy - 0.5} v8`} />
          <path d={`M${cx - 9} ${cy + 1} v6 a9 4 0 0 0 18 0 v-6`} />
        </g>
      );
    if (t === "people")
      return (
        <g fill="none" stroke={purple} strokeWidth="2.2" strokeLinecap="round">
          <circle cx={cx - 6} cy={cy - 5} r="4.5" />
          <circle cx={cx + 6} cy={cy - 5} r="4.5" />
          <path d={`M${cx - 13} ${cy + 10} q7 -11 14 0`} />
          <path d={`M${cx - 1} ${cy + 10} q7 -11 14 0`} />
        </g>
      );
    return (
      <g fill="none" stroke={purple} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d={`M${cx - 17} ${cy + 3} l9 -8 l8 5`} />
        <path d={`M${cx + 17} ${cy + 3} l-9 -8 l-8 5`} />
        <path d={`M${cx - 4} ${cy} l4 4 l4 -4`} />
      </g>
    );
  };
  const items = [
    { t: "building", label: "Wspólnota" },
    { t: "cap", label: "Uczelnia" },
    { t: "people", label: "Stowarzyszenie" },
    { t: "hands", label: "Spółdzielnia" }
  ];
  const pos = [
    { x: 170, y: 182 },
    { x: 350, y: 182 },
    { x: 170, y: 318 },
    { x: 350, y: 318 }
  ];
  return (
    <SvgWrap>
      {items.map((it, i) => (
        <motion.g key={i} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 + i * 0.1, type: "spring", stiffness: 180, damping: 13 }}>
          <rect x={pos[i].x - 70} y={pos[i].y - 52} width="140" height="104" rx="16" fill="#fff" stroke={purple} strokeWidth="2.5" />
          <circle cx={pos[i].x} cy={pos[i].y - 12} r="24" fill="#F1ECFB" />
          {icon(it.t, pos[i].x, pos[i].y - 12)}
          <text x={pos[i].x} y={pos[i].y + 34} textAnchor="middle" fontSize="14" fontWeight="800" fill={purple} fontFamily={FONT}>{it.label}</text>
        </motion.g>
      ))}
    </SvgWrap>
  );
};

// Placeholder „miejsce na ikonę" — dla scen jeszcze niezbudowanych.
const ScenePlaceholder: FC<{ tone?: SlideTone }> = ({ tone }) => {
  const dark = isDark(tone);
  const col = dark ? "rgba(255,255,255,0.55)" : "#B6ADCB";
  return (
    <Box position="absolute" inset="38px" borderRadius="24px" border="2px dashed" borderColor={col} display="flex" alignItems="center" justifyContent="center">
      <VStack spacing="14px">
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
          <rect x="7" y="7" width="66" height="66" rx="14" stroke={col} strokeWidth="3" strokeDasharray="6 7" />
          <circle cx="29" cy="29" r="7" stroke={col} strokeWidth="3" />
          <path d="M16 59 L35 39 L47 51 L57 41 L63 59 Z" stroke={col} strokeWidth="3" strokeLinejoin="round" fill="none" />
        </svg>
        <Text color={col} fontSize="15px" fontWeight="700" letterSpacing="0.02em">
          miejsce na ikonę
        </Text>
      </VStack>
    </Box>
  );
};

// ── Rejestr scen ────────────────────────────────────────────────────────────
export const getScene = (id: string | undefined, tone?: SlideTone) => {
  if (!id) return null;
  const dark = isDark(tone);
  const accent = dark ? C.gold : C.purple;
  switch (id) {
    case "ballot-seal":
      return <BallotSeal dark={dark} />;
    case "stamp-chain":
      return <StampChain accent={dark ? C.gold : C.purple} dark={dark} />;
    case "verify-lights":
      return <VerdictCard />;
    case "device-trio":
      return <FrontPhones />;
    case "institution":
      return <Institution accent={accent} dark={dark} />;
    case "trust-question":
      return <TrustQuestion accent={C.red} dark={dark} />;
    case "black-box":
      return <BlackBox />;
    case "launch":
      return <Launch accent={dark ? "#fff" : C.red} />;
    case "civic-hero":
      return <CivicHero />;
    case "turnout-gauge":
      return <TurnoutGauge />;
    case "participatory-budget":
      return <ParticipatoryBudget />;
    case "consultation":
      return <Consultation />;
    case "council-vote":
      return <CouncilVote />;
    case "cost-compare":
      return <CostCompare />;
    case "sovereign-hero":
      return <SovereignHero />;
    case "foreign-dep":
      return <ForeignDep />;
    case "own-chain":
      return <OwnChain />;
    case "open-code":
      return <OpenCode />;
    case "node-cluster":
      return <NodeCluster />;
    case "data-pl":
      return <DataPl />;
    case "secret-hero":
      return <SecretHero />;
    case "paradox":
      return <Paradox />;
    case "zkp-witness":
      return <ZkpWitness />;
    case "nullifier":
      return <Nullifier />;
    case "separation":
      return <Separation />;
    case "privacy-shield":
      return <PrivacyShield />;
    case "flow-hero":
      return <FlowHero />;
    case "vote-flow":
      return <VoteFlow />;
    case "step-vote":
      return <StepVote />;
    case "step-hash":
      return <StepHash />;
    case "step-seal":
      return <StepSeal />;
    case "two-modes":
      return <TwoModes />;
    case "roadmap":
      return <Roadmap />;
    case "community-hero":
      return <CommunityHero />;
    case "paper-pain":
      return <PaperPain />;
    case "org-types":
      return <OrgTypes />;
    default:
      return <ScenePlaceholder tone={tone} />;
  }
};
